import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteConfigurationMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function OperationAuthorizationsOverview() {
  const navigate = useNavigate();

  const [deleteConfiguration] = useDeleteConfigurationMutation();

  const handleDeleteProcessingItem = (id: string, refreshData: () => void) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteConfiguration({id})
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError),
      text: i18n.t('entity.accounting.labels.deleteOperationAuthorizationText'),
    });

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () =>
        navigate(
          composePath(settingsRoutes.accountingOperationAuthorizationDetail, {
            params: {id},
          })
        )
      )
      .with('delete', () => handleDeleteProcessingItem(id, refreshData))
      .otherwise(() => undefined);
  };

  const title = i18n.t('page.accounting.labels.operationAuthorizationsOverview');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              title: i18n.t('page.accounting.actions.addDefinition'),
              onClick: () => navigate(settingsRoutes.accountingOperationAuthorizationNew),
            },
          ],
        }}
        data-testid={testIds.settings.accountingOperationAuthorizationsOverview('page')}
      >
        <DmsDataGrid
          gridCode="accounting-operation-authorizations"
          actionCallback={actionCallback}
          data-testid={testIds.settings.accountingOperationAuthorizationsOverview('dataGrid')}
        />
      </SettingsTemplate>
    </>
  );
}
